// Mini App shell: auth, screen routing, deep links, host viewport wiring.
//
// Split out of a single 3,081-line file — the pieces live in ./miniapp/
// (shared modules) and ./miniapp/screens/ (one file per screen).
import { useState, useMemo, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import Onboarding from "./Onboarding.jsx";
import { tt, useLang } from "./i18n.jsx";
import { initSquircles } from "./squircle.js";
import { pauseAnimationsWhenHidden } from "./device.js";
import { api } from "./miniapp/api.js";
import { FluentEmoji } from "./miniapp/emoji.jsx";
import { enteredKey, hasDismissedJoinGate, hostKey, markJoinGateDismissed, onboardKey, platformInfo, setPlatformInfo, webApp } from "./miniapp/platform.js";
import { bazaarSignIn, bazaarSilentSignIn, unconsumedPurchases } from "./miniapp/android.js";
import { FIRST_RUN_CSS } from "./miniapp/faces.jsx";
import { useHostBackButton } from "./miniapp/backbutton.js";
import { useFullscreen, useSettingsButton } from "./miniapp/host.js";
import { useAutoUpdate } from "./miniapp/update.js";
import { SettingsSheet, SupportSheet } from "./miniapp/screens/Settings.jsx";
import { GuaranteeSheet } from "./miniapp/screens/Guarantee.jsx";
import { SeasonCheckinModal } from "./miniapp/screens/SeasonCheckin.jsx";
import { AddChildScreen, EditChildScreen } from "./miniapp/screens/Child.jsx";
import { HomeScreen, ReferralModal } from "./miniapp/screens/Home.jsx";
import { JourneyScreen } from "./miniapp/screens/Journey.jsx";
// The games (art + seven games) load ONLY when «بازی‌ها» is opened — they are
// ~80 KB the first paint in a messenger WebView shouldn't have to wait for.
// The 3D tale library (three.js) — its own lazy chunk, warmed in idle time from
// Home (TaleShelf) so it is usually ready before the parent taps.
const TaleLibrary = lazy(() => import("./miniapp/tales/Library.jsx").then(m => ({ default: m.TaleLibrary })));
const GamesScreen = lazy(() => import("./miniapp/games/index.jsx").then(m => ({ default: m.GamesScreen })));
import { StoryPlayerScreen } from "./miniapp/screens/Player.jsx";
import { QuestionnaireScreen } from "./miniapp/screens/Questionnaire.jsx";
import { SubscriptionScreen } from "./miniapp/screens/Subscription.jsx";
import { PaymentSuccessScreen, QuestionnaireDoneScreen } from "./miniapp/screens/Success.jsx";
import { TermsScreen } from "./miniapp/screens/Terms.jsx";
import { ChannelJoinScreen } from "./miniapp/screens/Welcome.jsx";
import { C, BASE_CSS, MINIAPP_CSS, WHITE, TALE, GAME_SKY } from "./design-system.js";

// ── App Shell ─────────────────────────────────────────────────────────────────
export default function App() {
  const { lang, dir } = useLang();
  // ⚠️ Starts at "loading", NOT "welcome". The channel gate must only ever be a
  // SERVER VERDICT, never a default and never an error state. It used to be the
  // initial value, so (a) every launch flashed the join screen before
  // /auth/login answered and (b) any login failure — a dropped request in the
  // WebView, a stale initData — parked a parent who IS in the channel on it,
  // pressing «عضو شدم ✓» against a check that had never run.
  const [screen, setScreen] = useState("loading");
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedTale, setSelectedTale] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Set only by Home's «ارتقا» button; any other way into the plans screen is a
  // purchase/renewal, so it is cleared as soon as the parent leaves checkout.
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  useEffect(() => { if (screen !== "subscription" && screen !== "paymentSuccess") setUpgradeInfo(null); }, [screen]);
  const [showReferral, setShowReferral] = useState(false);
  // The between-seasons check-in (services/seasons.py). Fetched once after login;
  // `null` = none open. Dismissing it is free — an unanswered one auto-resolves
  // server-side, so this never gates anything.
  const [checkin, setCheckin] = useState(null);
  const [checkinDismissed, setCheckinDismissed] = useState(false);
  // Lifted here so the active child tab survives leaving/returning to home
  // (e.g. opening the questionnaire for the 2nd child and coming back).
  const [activeChildIdx, setActiveChildIdx] = useState(0);
  // Deep-link: the renewal DM button opens ?child=<id> (or start_param "child<id>")
  // → land on that child's tab. Applied once after the children list loads.
  const deepLinkChildId = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("child");
      const sp = (webApp()?.initDataUnsafe?.start_param || "").replace(/^child/, "");
      const n = parseInt(q || sp, 10);
      return isNaN(n) ? null : n;
    } catch { return null; }
  }, []);
  const deepLinkApplied = useRef(false);
  const [termsReturn, setTermsReturn] = useState("home");  // where «بازگشت» goes from Terms
  const [settingsOpen, setSettingsOpen] = useState(false);  // the host ⋮ settings sheet
  const [guaranteeOpen, setGuaranteeOpen] = useState(false); // «ضمانت بازگشت وجه» (from settings)
  const [supportOpen, setSupportOpen] = useState(false);
  // The parent tapped «عضویت در کانال» → re-check membership quietly until they
  // are in (there is no «عضو شدم» button to press any more).
  const [joinWatching, setJoinWatching] = useState(false);
  // First-launch onboarding carousel (once per device).
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(onboardKey()));

  // Auth + children state
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [children, setChildren] = useState([]);
  // A FREE family (no child has an active plan) sees Adly's card on Home and the
  // form screens — Eitaa only (AdSlot checks the host).
  const freeFamily = !children.some(c => !!c.active_subscription);
  const [userName, setUserName] = useState("");
  // The Bazaar app before «ورود با بازار»: home works, nothing parent-specific does.
  const [guest, setGuest] = useState(false);
  const [isChannelMember, setIsChannelMember] = useState(false);
  // Platform info as REACT STATE. Seeded from the injected host bridge so the very
  // first paint already has the right channel link, then replaced by the server's
  // authoritative platform_info at login. It must be state: the join screen is the
  // initial screen, and setScreen("welcome") after login is a no-op, so a plain
  // sessionStorage read would keep showing the pre-login (Bale) fallback.
  const [platform, setPlatform] = useState(() => platformInfo());

  // Once the children list is loaded, jump to the deep-linked child's tab (once).
  useEffect(() => {
    if (deepLinkApplied.current || !deepLinkChildId || !children.length) return;
    const idx = children.findIndex(c => c.id === deepLinkChildId);
    if (idx >= 0) setActiveChildIdx(idx);
    deepLinkApplied.current = true;
  }, [deepLinkChildId, children]);

  // Authenticate via Bale/Telegram initData.
  // `silent` re-checks without the full-screen loader — the join screen's automatic
  // membership re-check, which must stay on that screen instead of flashing the
  // whole app out and back. `quiet` also keeps a failed re-check off the screen.
  // Returns the login payload so the caller can act on `is_channel_member`.
  async function authenticate({ silent = false, quiet = false } = {}) {
    if (hostKey() === "bazaar") return authenticateBazaar();
    if (!silent) setAuthLoading(true);
    setAuthError(null);
    try {
      // Read from whichever WebApp bridge the host injected (Bale exposes the
      // Telegram-compatible global; some builds also expose window.Bale).
      const wa = webApp();
      const initData = wa?.initData || "";
      if (!initData) {
        const existingToken = sessionStorage.getItem("jwt");
        if (existingToken) {
          await fetchChildren();
          setScreen("home");
          return;
        }
        throw new Error(tt("داده‌های ورود یافت نشد. لطفاً برنامه را از داخل ربات باز کنید."));
      }
      const webAppUser = wa?.initDataUnsafe?.user;
      if (webAppUser?.first_name) setUserName(webAppUser.first_name);
      let data;
      try {
        data = await api("/auth/login", {
          method: "POST",
          body: JSON.stringify({ init_data: initData, lang }),
        });
      } catch (loginErr) {
        // The host's signature didn't verify. Soroush signs a WARM reopen with a
        // key nobody can check (a cold launch verifies), so fall back to the session
        // a verified login left on this device — our own JWT, which the server
        // checks — but only for the SAME messenger user the host says is here now.
        data = await resumeSession(webAppUser?.id);
        if (!data) throw loginErr;
      }
      rememberSession(data.access_token, webAppUser?.id);
      return await applySession(data);
    } catch (e) {
      if (!quiet) setAuthError(e.message || tt("خطا در احراز هویت"));
      return null;
    } finally {
      setAuthLoading(false);
    }
  }

  // Everything that follows a successful sign-in, whatever proved who the parent is
  // (messenger initData, our own JWT, or «ورود با بازار»). `navigate: false` keeps the
  // current screen — the Bazaar app signs in from INSIDE step 1 or the plans page.
  async function applySession(data, { navigate = true } = {}) {
    sessionStorage.setItem("jwt", data.access_token);
    setGuest(false);
    // The backend tells us which platform signed the initData, plus everything
    // platform-specific (messenger name, channel link, whether membership can be
    // verified, whether in-app payment exists). Stored BEFORE any screen decision
    // so the join screen renders this platform's channel, not Bale's.
    sessionStorage.setItem("platform", data.platform || "bale");
    if (data.platform_info) { setPlatformInfo(data.platform_info); setPlatform(data.platform_info); }
    if (data.first_name) setUserName(data.first_name);
    setIsChannelMember(data.is_channel_member);
    // Login ships the children list — skip the extra round-trip when present.
    if (Array.isArray(data.children)) setChildren(data.children);
    else await fetchChildren();
    if (!navigate) return data;

    // The «ورود» screen is for FIRST-TIME visitors only. A member who already
    // has a plan, already added a child, or has passed this screen before goes
    // straight home — re-showing an intro screen to a returning parent reads
    // like they were signed out.
    let enteredBefore = false;
    try { enteredBefore = localStorage.getItem(enteredKey()) === "1"; } catch (_) {}
    const returning = data.has_active_subscription
      || (Array.isArray(data.children) && data.children.length > 0)
      || enteredBefore;
    // Gate decision. On verified platforms the server's verdict rules. On a
    // platform with no membership API the server always says "member", so fall
    // back to the trust gate: show it once, then never again for this user.
    const info = data.platform_info || platformInfo();
    const enforced = info.channel_enforced !== false;
    const gateBlocks = enforced
      ? !data.is_channel_member
      : (!!info.channel && !hasDismissedJoinGate() && !returning);

    if (gateBlocks) {
      setScreen("welcome");
    } else if (returning) {
      try { localStorage.setItem(enteredKey(), "1"); } catch (_) {}
      setScreen("home");
    } else {
      // First visit: straight in — the old «ورود به ضحا قصه» intro was redundant.
      try { localStorage.setItem(enteredKey(), "1"); } catch (_) {}
      setScreen("home");
    }
    return data;
  }

  // ── The Bazaar Android app ────────────────────────────────────────────────
  // No messenger, so no initData: the parent signs in with «ورود با بازار». A
  // returning parent (who granted it before) is signed in SILENTLY here; a new one
  // lands on home as a GUEST and signs in as part of step 1 (or before paying) —
  // product owner, 2026-10-29. There is no channel and no join gate in the app.
  async function authenticateBazaar() {
    setAuthLoading(true);
    setAuthError(null);
    const info = platformInfo();
    setPlatformInfo(info); setPlatform(info);
    sessionStorage.setItem("platform", "bazaar");
    try {
      const data = await bazaarSilentSignIn(api);
      if (data) { await applySession(data); recoverBazaarPurchases(); return data; }
    } catch (e) {
      console.warn("[bazaar] silent sign-in failed:", e?.message || e);
    } finally {
      setAuthLoading(false);
    }
    sessionStorage.removeItem("jwt");
    setGuest(true);
    setChildren([]);
    setScreen("home");
    return null;
  }

  // Interactive «ورود با بازار» — called by step 1 and the plans page. Resolves true
  // once signed in; false when the parent closed Bazaar's page.
  const signInWithBazaar = useCallback(async () => {
    const data = await bazaarSignIn(api);
    if (!data) return false;
    await applySession(data, { navigate: false });
    recoverBazaarPurchases();
    return true;
  }, []);

  // A Bazaar payment whose verify never reached us (the app died mid-purchase, the
  // network dropped) is still UNCONSUMED in Bazaar — hand each one to the server
  // again. The server asks Bazaar, so nothing here is trusted.
  async function recoverBazaarPurchases() {
    const list = await unconsumedPurchases();
    for (const p of list) {
      const subId = parseInt(p.payload, 10);
      if (!subId || !p.purchaseToken) continue;
      try {
        await api("/payment/bazaar/verify", { method: "POST", body: JSON.stringify({
          subscription_id: subId, purchase_token: p.purchaseToken, sku: p.productId,
          original_json: p.originalJson || null, signature: p.dataSignature || null }) });
        refreshChildren();
      } catch (e) { console.warn("[bazaar] recovery failed:", e?.message || e); }
    }
  }

  // The last verified session on this device, for resumeSession(). Keyed to the
  // messenger user id so a different account in the same WebView never inherits it.
  function rememberSession(token, uid) {
    try { if (token && uid) localStorage.setItem("zg_session", JSON.stringify({ token, uid: String(uid) })); } catch (_) {}
  }
  async function resumeSession(uid) {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem("zg_session") || "null"); } catch (_) {}
    if (!saved?.token || !uid || saved.uid !== String(uid)) return null;
    sessionStorage.setItem("jwt", saved.token);
    try {
      return await api("/auth/resume", { method: "POST" });
    } catch (_) {
      sessionStorage.removeItem("jwt");
      try { localStorage.removeItem("zg_session"); } catch (_) {}
      return null;
    }
  }

  async function fetchChildren() {
    try {
      const data = await api("/children");
      setChildren(data || []);
      return data || [];
    } catch {
      setChildren([]);
      return [];
    }
  }

  const refreshChildren = useCallback(() => fetchChildren(), []);

  // Pull the open season check-in once the parent has a subscribed child. One
  // extra round-trip, and only for subscribers — the pre-purchase funnel (where
  // every RTT costs conversion) is untouched.
  const refreshCheckin = useCallback(async () => {
    try { setCheckin(await api("/seasons/checkin")); } catch { setCheckin(null); }
  }, []);

  useEffect(() => {
    // Squircle corners (superellipse, same as the emoji avatar) with working
    // borders — runtime engine, see src/squircle.js.
    initSquircles();
    pauseAnimationsWhenHidden();
    const style = document.createElement("style");
    style.id = "zoha-style";
    style.textContent = `${BASE_CSS}${MINIAPP_CSS}${FIRST_RUN_CSS}`;
    if (!document.getElementById("zoha-style")) document.head.appendChild(style);

    // ── Tiered tap feedback ──
    const SPARK_COLORS = [C.gold, C.goldLight, C.teal, C.purple, C.cream];
    function onPress(e) {
      const btn = e.target.closest(".press, .emojiPick, .playBtn");
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const px = (e.touches ? e.touches[0].clientX : e.clientX);
      const py = (e.touches ? e.touches[0].clientY : e.clientY);
      const x = px - rect.left;
      const y = py - rect.top;

      const ring = document.createElement("span");
      ring.className = "ripple-ring";
      const size = Math.max(rect.width, rect.height) * 1.5;
      ring.style.width = ring.style.height = `${size}px`;
      ring.style.left = `${x}px`;
      ring.style.top = `${y}px`;
      const prevPos = getComputedStyle(btn).position;
      if (prevPos === "static") btn.style.position = "relative";
      const prevOverflow = getComputedStyle(btn).overflow;
      btn.style.overflow = "hidden";
      btn.appendChild(ring);
      setTimeout(() => { ring.remove(); btn.style.overflow = prevOverflow; }, 550);

      if (btn.classList.contains("celebrate")) {
        const sparkCount = 10;
        for (let i = 0; i < sparkCount; i++) {
          const s = document.createElement("span");
          s.className = "spark";
          const angle = (Math.PI * 2 * i) / sparkCount + Math.random() * 0.5;
          const dist = 30 + Math.random() * 30;
          s.style.left = `${px}px`;
          s.style.top = `${py}px`;
          s.style.setProperty("--dx", `${Math.cos(angle) * dist}px`);
          s.style.setProperty("--dy", `${Math.sin(angle) * dist}px`);
          s.style.background = SPARK_COLORS[i % SPARK_COLORS.length];
          s.style.width = s.style.height = `${5 + Math.random() * 5}px`;
          document.body.appendChild(s);
          setTimeout(() => s.remove(), 560);
        }
      }
    }
    document.addEventListener("pointerdown", onPress);

    return () => {
      const el = document.getElementById("zoha-style"); if (el) el.remove();
      document.removeEventListener("pointerdown", onPress);
    };
  }, []);

  // Auto-authenticate on mount
  useEffect(() => { authenticate(); }, []);

  // Host WebApp: expand to full height + track fullscreen safe-area insets so our
  // header/buttons never sit under the messenger's top buttons or the device's
  // status/navigation bars. Insets are published as CSS vars consumed by .app-root.
  // Applies to every host (Bale/Telegram/Eitaa all expose this same surface).
  useEffect(() => {
    const tg = webApp();
    if (!tg) return;
    try { tg.ready(); tg.expand(); } catch (_) {}
    try { tg.setBackgroundColor?.(C.bgDeep); tg.setHeaderColor?.(C.bgDeep); } catch (_) {}
    const apply = () => {
      const sa = tg.safeAreaInset || {};
      const ca = tg.contentSafeAreaInset || {};   // inset from Telegram's own UI, within the safe area
      const top = (sa.top || 0) + (ca.top || 0);
      const bottom = (sa.bottom || 0) + (ca.bottom || 0);
      const root = document.documentElement;
      root.style.setProperty("--tg-safe-top", top + "px");
      root.style.setProperty("--tg-safe-bottom", bottom + "px");
    };
    apply();
    const events = ["safeAreaChanged", "contentSafeAreaChanged", "viewportChanged", "fullscreenChanged"];
    events.forEach(e => tg.onEvent?.(e, apply));
    return () => events.forEach(e => tg.offEvent?.(e, apply));
  }, []);

  // Go FULLSCREEN where the host supports it (Eitaa/Telegram v1.3+). The host then
  // paints its own close/menu buttons over the page, which is exactly what the
  // safe-area insets above describe — they are re-read on `fullscreenChanged`, so
  // the layout follows automatically. A host that refuses (`fullscreenFailed`:
  // UNSUPPORTED / ALREADY_FULLSCREEN) just stays expanded, which is the correct
  // fallback, so there is nothing to handle beyond not crashing.
  useFullscreen(true);

  // The host's ⋮ «تنظیمات» item opens the settings sheet (language + terms). It
  // used to jump straight to the terms screen, which made language a control
  // floating at the bottom of two screens instead of a setting. No-ops on a host
  // without a SettingsButton — those keep the in-page row (see `hasSettingsButton`).
  const openTerms = useCallback(() => {
    setTermsReturn("home");
    setSettingsOpen(false);
    setScreen("terms");
  }, [screen]);
  useSettingsButton(() => setSettingsOpen(true), screen !== "terms" && !showOnboarding);

  useEffect(() => {
    if (screen === "home" && children.some(c => c.active_subscription)) refreshCheckin();
  }, [screen, children, refreshCheckin]);

  function goHome() {
    refreshChildren();
    setScreen("home");
  }

  // The host's native BackButton (Eitaa/Telegram/Soroush show a ‹ in their own
  // header; Eitaa flagged its absence). It mirrors each screen's in-page
  // «بازگشت» — one map, so the two can never disagree. Screens with their own
  // internal steps (the questionnaire) register a deeper handler that wins.
  const SCREEN_BACK = {
    terms:             () => setScreen(termsReturn),
    addChild:          goHome,
    editChild:         goHome,
    subscription:      goHome,
    questionnaire:     goHome,   // overridden per-step from inside the wizard
    journey:           goHome,
    games:             goHome,   // a game in progress registers a deeper handler
    tale:              goHome,
    player:            goHome,
    paymentSuccess:    goHome,
    questionnaireDone: goHome,
  };
  // welcome / enter / home are roots — no back arrow there.
  useHostBackButton(SCREEN_BACK[screen], { active: !showOnboarding && !!SCREEN_BACK[screen] });

  // «افزودن فرزند». A plan bought BEFORE any child exists sits on a blank
  // placeholder child (routers/children.placeholder_child) — adding a child then
  // means filling THAT one in, or the plan would stay on a child nobody named.
  function addOrCompleteChild() {
    const blank = children.find(c => !c.name);
    if (blank) { setSelectedChild(blank); setScreen("editChild"); }
    else setScreen("addChild");
  }

  // After adding a child, land on the NEW child's tab (not back on the first).
  async function handleChildAdded(newChild) {
    const list = await fetchChildren();
    const idx = newChild?.id ? list.findIndex(c => c.id === newChild.id) : -1;
    setActiveChildIdx(idx >= 0 ? idx : Math.max(0, list.length - 1));
    setScreen("home");
  }

  // The join link was tapped. Where membership can't be verified (Eitaa/Soroush)
  // the tap is taken on trust, once — exactly what the old «عضو شدم» tap did there.
  // Where it CAN be, we just start watching: see the effect below.
  function handleJoinTapped() {
    if (platformInfo().channel_enforced === false) {
      markJoinGateDismissed();
      setTimeout(() => authenticate({ silent: true, quiet: true }), 800);
      return;
    }
    setJoinWatching(true);
  }

  // While the gate is up, re-ask the server whenever the parent comes BACK to the
  // app (they joined in the messenger and returned) and, after they tapped the
  // link, every few seconds for a while. A member goes straight in — authenticate()
  // routes them — and a non-member simply stays on the gate.
  useEffect(() => {
    if (screen !== "welcome") { setJoinWatching(false); return; }
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try { await authenticate({ silent: true, quiet: true }); } finally { busy = false; }
    };
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    let id = null, stop = null;
    if (joinWatching) {
      id = setInterval(check, 4000);
      stop = setTimeout(() => { clearInterval(id); setJoinWatching(false); }, 180000);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      if (id) clearInterval(id);
      if (stop) clearTimeout(stop);
    };
  }, [screen, joinWatching]); // eslint-disable-line react-hooks/exhaustive-deps

  // A newer deploy is picked up on its own — but only on home with nothing open,
  // so a half-filled questionnaire or a payment in progress is never reloaded away.
  useAutoUpdate(screen === "home" && !showOnboarding && !settingsOpen && !supportOpen && !guaranteeOpen
    && !showReferral && !(checkin && !checkinDismissed));

  // First-launch onboarding carousel — shown before anything else, once per device.
  if (showOnboarding) {
    return (
      <div className="app-root app-root--bleed" style={{ maxWidth: 430, margin: "0 auto", minHeight: "100dvh", position: "relative" }}>
        <Onboarding onFinish={() => { localStorage.setItem(onboardKey(), "1"); setShowOnboarding(false); }} />
      </div>
    );
  }

  // Neutral pre-verdict state: the login call is in flight, or it failed before it
  // ever told us anything about this user. Either way we know nothing about their
  // membership yet, so we must not render the gate.
  const preVerdict = authLoading || screen === "loading";
  if (preVerdict) {
    const failed = !authLoading && !!authError;
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100dvh", paddingTop: "var(--tg-safe-top)", paddingBottom: "var(--tg-safe-bottom)", background: C.bgDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, direction: dir }}>
        <FluentEmoji anim="float" size={56} style={{ marginBottom: 16 }}>🌙</FluentEmoji>
        <div style={{ color: failed ? C.red : C.cream, fontSize: 18, fontWeight: 700, textAlign: "center", lineHeight: 1.8 }}>
          {failed ? authError : tt("در حال ورود...")}
        </div>
        {failed ? (
          <button onClick={() => authenticate()} className="press"
            style={{ marginTop: 20, background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, fontWeight: 800, fontSize: 16, padding: "13px 34px", borderRadius: 14, border: "none", cursor: "pointer" }}>
            {tt("تلاش دوباره")}
          </button>
        ) : (
          <div style={{ color: C.textMid, fontSize: 12.5, marginTop: 10, maxWidth: 270, textAlign: "center", lineHeight: 1.7 }}>
            «ضحا» یعنی روشناییِ گرمِ صبحگاه که تاریکی را می‌زداید.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={screen === "games" || screen === "tale" ? "app-root app-root--bleed" : "app-root"} style={{ maxWidth: 430, margin: "0 auto", minHeight: "100dvh", position: "relative", direction: dir }}>
      {authError && (
        <div className="auth-error" style={{ position: "fixed", bottom: "calc(40px + var(--tg-safe-bottom))", left: "50%", transform: "translateX(-50%)", background: C.red, color: WHITE, padding: "12px 24px", borderRadius: 12, fontSize: 14, zIndex: 100 }}>
          {authError}
        </div>
      )}
      {screen === "welcome" && <ChannelJoinScreen onJoinTapped={handleJoinTapped} platform={platform} watching={joinWatching} />}
      {screen === "terms" && <TermsScreen onBack={() => setScreen(termsReturn)} />}
      {screen === "home" && (
        <HomeScreen
          children={children}
          refreshChildren={refreshChildren}
          userName={userName}
          activeChild={activeChildIdx}
          setActiveChild={setActiveChildIdx}
          onAddChild={addOrCompleteChild}
          onSelectStory={(s, c) => { setSelectedStory(s); setSelectedChild(c); setScreen("player"); }}
          onSubscribe={(c) => { setUpgradeInfo(null); setSelectedChild(c); setScreen("subscription"); }}
          onUpgrade={(c, u) => { setUpgradeInfo(u); setSelectedChild(c); setScreen("subscription"); }}
          onQuestionnaire={(c) => { setSelectedChild(c); setScreen("questionnaire"); }}
          onJourney={(c) => { setSelectedChild(c); setScreen("journey"); }}
          onGames={(c) => { setSelectedChild(c); setScreen("games"); }}
          onTale={(t, c) => { setSelectedChild(c); setSelectedTale(t.key); setScreen("tale"); }}
          onEditChild={(c) => { setSelectedChild(c); setScreen("editChild"); }}
          onReferral={() => setShowReferral(true)}
          onSupport={() => setSupportOpen(true)}
          onTerms={() => { setTermsReturn("home"); setScreen("terms"); }}
          onGuarantee={guest ? null : () => setGuaranteeOpen(true)}
          guest={guest}
        />
      )}
      {screen === "addChild" && <AddChildScreen onBack={goHome} onDone={handleChildAdded} onSignIn={guest ? signInWithBazaar : null} ads={freeFamily} />}
      {screen === "editChild" && selectedChild && <EditChildScreen child={selectedChild} onBack={goHome} onSave={goHome} ads={freeFamily} />}
      {/* A plan may be bought before any child exists (step 3 needs no other step):
          then there is no id and the server puts it on a placeholder child. */}
      {screen === "subscription" && (
        <SubscriptionScreen
          childId={selectedChild?.id || null}
          childName={selectedChild?.name || ""}
          activeSub={selectedChild?.active_subscription || null}
          upgrade={upgradeInfo}
          onSignIn={guest ? signInWithBazaar : null}
          onBack={goHome}
          onPay={plan => { setSelectedPlan(plan); setScreen("paymentSuccess"); }}
        />
      )}
      {screen === "questionnaire" && selectedChild && (
        <QuestionnaireScreen
          childId={selectedChild.id}
          child={selectedChild}
          onBack={goHome}
          ads={freeFamily}
          onDone={() => { refreshChildren(); setScreen("questionnaireDone"); }}
        />
      )}
      {screen === "journey" && selectedChild && <JourneyScreen child={selectedChild} onBack={goHome} />}
      {screen === "tale" && selectedChild && (
        <Suspense fallback={<div style={{ minHeight: "100dvh", background: TALE.night }} />}>
          <TaleLibrary child={selectedChild} focusKey={selectedTale} onBack={goHome}
            onLocked={() => setScreen("subscription")} />
        </Suspense>
      )}
      {screen === "games" && selectedChild && (
        <Suspense fallback={<div style={{ minHeight: "100dvh", background: GAME_SKY }} />}>
          <GamesScreen child={selectedChild} onBack={goHome} onAddChild={addOrCompleteChild}
            onSubscribe={(c) => { setSelectedChild(c); setScreen("subscription"); }}
            onQuestionnaire={(c) => { setSelectedChild(c); setScreen("questionnaire"); }} />
        </Suspense>
      )}
      {screen === "player" && selectedStory && selectedChild && <StoryPlayerScreen story={selectedStory} child={selectedChild} onBack={goHome} />}
      {screen === "paymentSuccess" && <PaymentSuccessScreen plan={selectedPlan} childName={selectedChild?.name}
        ready={!!selectedChild?.questionnaire_completed} onDone={goHome} />}
      {screen === "questionnaireDone" && (
        <QuestionnaireDoneScreen
          needsPlan={!selectedChild?.active_subscription}
          childName={selectedChild?.name}
          onChoosePlan={() => setScreen("subscription")}
          onDone={goHome}
        />
      )}
      {showReferral && <ReferralModal onClose={() => setShowReferral(false)} />}
      {settingsOpen && <SettingsSheet subscribed={children.some(c => !!c.active_subscription)} onClose={() => setSettingsOpen(false)} onTerms={openTerms} onSupport={() => { setSettingsOpen(false); setSupportOpen(true); }}
        onGuarantee={guest ? null : () => { setSettingsOpen(false); setGuaranteeOpen(true); }} />}
      {guaranteeOpen && <GuaranteeSheet onClose={() => setGuaranteeOpen(false)} onChanged={refreshChildren} />}
      {supportOpen && <SupportSheet subscribed={children.some(c => !!c.active_subscription)} onClose={() => setSupportOpen(false)} />}
      {/* The check-in sits over HOME only — never over a flow the parent is mid-way
          through, and never over the pre-purchase funnel. */}
      {checkin && !checkinDismissed && screen === "home" && !showReferral && (
        <SeasonCheckinModal
          checkin={checkin}
          onClose={() => setCheckinDismissed(true)}
          onDone={() => { setCheckin(null); setCheckinDismissed(true); refreshChildren(); }}
        />
      )}
    </div>
  );
}
